import axios from "axios";

const BASE_URL = (process.env.REACT_APP_BASE_URL || "https://services.allhomedesire.in").replace(/\/+$/, "");

/**
 * Helper to build common headers with authorization
 */
const getAuthHeaders = (token) => {
  const headers = {
    "Content-Type": "application/json",
    Accept: "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

/**
 * 1. OTP REQUEST
 * POST /all/otp/request
 */
export const requestPartnerOtp = async (mobile, action = "register") => {
  const formattedMobile = String(mobile).startsWith("91")
    ? String(mobile)
    : `91${mobile}`;

  const response = await axios.post(`${BASE_URL}/all/otp/request`, {
    mobile: formattedMobile,
    action,
  });
  return response.data;
};

/**
 * 2. OTP VERIFICATION
 * POST /all/otp/verify
 */
export const verifyPartnerOtp = async (mobile, otp) => {
  const formattedMobile = String(mobile).startsWith("91")
    ? String(mobile)
    : `91${mobile}`;

  const response = await axios.post(`${BASE_URL}/all/otp/verify`, {
    mobile: formattedMobile,
    otp: String(otp).trim(),
    user_type: "PARTNER",
  });
  return response.data;
};

/**
 * 3. REFRESH TOKEN
 * POST /all/refresh-token
 */
export const refreshPartnerToken = async (refreshToken) => {
  const response = await axios.post(`${BASE_URL}/all/refresh-token`, {
    refreshToken,
  });
  return response.data;
};

/**
 * 4. FETCH PARTNER SERVICES
 * POST /admin/services
 */
export const fetchPartnerServices = async (token, pageNumber = 1, pageSize = 50) => {
  const response = await axios.post(
    `${BASE_URL}/admin/services`,
    {
      page_number: pageNumber,
      page_size: pageSize,
      order_by: "ASC",
    },
    {
      headers: getAuthHeaders(token),
    }
  );
  return response.data;
};

/**
 * 5. FETCH SERVICE SUB-CATEGORIES
 * For Service Partners: POST /api/all/services/types
 * For Local Bazaar Partners: POST /api/local/bazaar/categories/list
 */
export const fetchServiceTypes = async (token, pageNumber = 1, pageSize = 50) => {
  const response = await axios.post(
    `${BASE_URL}/api/all/services/types`,
    {
      page_number: pageNumber,
      page_size: pageSize,
    },
    {
      headers: getAuthHeaders(token),
    }
  );
  return response.data;
};

export const fetchLocalBazaarCategories = async (token, pageNumber = 1, pageSize = 50) => {
  const response = await axios.post(
    `${BASE_URL}/api/local/bazaar/categories/list`,
    {
      page_number: pageNumber,
      page_size: pageSize,
    },
    {
      headers: getAuthHeaders(token),
    }
  );
  return response.data;
};

/**
 * 6. COMPLETE / UPDATE PARTNER PROFILE
 * PUT /admin/editProfile
 */
export const updatePartnerProfile = async (token, profileData) => {
  const response = await axios.put(`${BASE_URL}/admin/editProfile`, profileData, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

/**
 * 7. FETCH PARTNER SUBSCRIPTION STATUS
 * GET /api/v1/subscriptions/my-subscription
 */
export const fetchMySubscription = async (token) => {
  const response = await axios.get(`${BASE_URL}/api/v1/subscriptions/my-subscription`, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

/**
 * 7b. FETCH SUBSCRIPTION PLANS (FOR PARTNERS)
 * POST /api/v1/subscription-plans/plans or /api/v1/subscription-plans/service
 */
export const fetchSubscriptionPlans = async (token, serviceId = null) => {
  const normalizePlan = (p) => {
    if (!p) return null;
    const rawPrice = p.price_paise ? p.price_paise / 100 : (p.price !== undefined ? Number(p.price) : Number(p.total_price || 0));
    const totalPrice = p.total_price !== undefined ? Number(p.total_price) : rawPrice;
    return {
      ...p,
      id: p.id || p.subscription_plan_id || p._id || p.plan_id,
      name: p.name || p.title || "Partner Growth Plan",
      price: rawPrice,
      total_price: totalPrice,
      duration_in_days: p.duration_days || p.duration_in_days || p.duration || 30,
      trial_days: p.trial_days || p.trial_period || p.free_trial_days || (p.free_trial ? 7 : null),
      gst: p.gst !== undefined ? Number(p.gst) : Math.round(rawPrice * 0.18),
      features: Array.isArray(p.features) ? p.features : [],
    };
  };

  // 1. Try service-specific endpoint /api/v1/subscription-plans/service
  if (serviceId) {
    try {
      const response = await axios.post(
        `${BASE_URL}/api/v1/subscription-plans/service`,
        {
          page_number: 1,
          page_size: 50,
          service_id: Number(serviceId),
        },
        { headers: getAuthHeaders(token) }
      );
      const list = response.data?.plans || response.data?.subscriptionPlans || response.data?.data || [];
      if (Array.isArray(list) && list.length > 0) {
        return list.map(normalizePlan);
      }
    } catch (err) {
      console.warn("Service plans /api/v1/subscription-plans/service failed, trying alternate routes:", err.message);
    }

    // 2. Try alternate service plans route: /api/subscription/plan/list/paid/by/service
    try {
      const response = await axios.post(
        `${BASE_URL}/api/subscription/plan/list/paid/by/service`,
        {
          all_service_id: Number(serviceId),
          service_id: Number(serviceId),
          pageNumber: 1,
          pageSize: 50,
        },
        { headers: getAuthHeaders(token) }
      );
      const list = response.data?.subscriptionPlans || response.data?.plans || response.data?.data || [];
      if (Array.isArray(list) && list.length > 0) {
        return list.map(normalizePlan);
      }
    } catch (err) {
      console.warn("Alternate service plans route failed:", err.message);
    }
  }

  // 3. Try /api/v1/subscription-plans/plans
  try {
    const fallbackResponse = await axios.post(
      `${BASE_URL}/api/v1/subscription-plans/plans`,
      { page_number: 1, page_size: 50 },
      { headers: getAuthHeaders(token) }
    );
    const list = fallbackResponse.data?.plans || fallbackResponse.data?.subscriptionPlans || fallbackResponse.data?.data || [];
    if (Array.isArray(list) && list.length > 0) {
      return list.map(normalizePlan);
    }
  } catch (error) {
    console.warn("/api/v1/subscription-plans/plans failed:", error.message);
  }

  // 4. Try legacy payment details route: /api/payments/subscription/details
  try {
    const detailsRes = await axios.get(`${BASE_URL}/api/payments/subscription/details`, {
      headers: getAuthHeaders(token),
    });
    const list = detailsRes.data?.subscriptionPlans || detailsRes.data?.plans || [];
    if (Array.isArray(list) && list.length > 0) {
      return list.map(normalizePlan);
    }
  } catch (error) {
    console.warn("/api/payments/subscription/details failed:", error.message);
  }

  return [];
};

/**
 * 7c. START PARTNER TRIAL
 * POST /api/v1/subscriptions/start-trial
 */
export const startPartnerTrial = async (token, { planId, seats = 1, couponCode = null }) => {
  const payload = {
    plan_id: Number(planId),
    seats: Number(seats) || 1,
  };
  if (couponCode) {
    payload.coupon_code = couponCode;
  }
  const response = await axios.post(`${BASE_URL}/api/v1/subscriptions/start-trial`, payload, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

/**
 * 7d. CREATE PARTNER SUBSCRIPTION
 * POST /api/v1/subscriptions/create
 */
export const createPartnerSubscription = async (token, { userId, planId, seats = 1, couponCode = null }) => {
  const payload = {
    user_id: Number(userId),
    plan_id: Number(planId),
    seats: Number(seats) || 1,
  };
  if (couponCode) {
    payload.coupon_code = couponCode;
  }
  const response = await axios.post(`${BASE_URL}/api/v1/subscriptions/create`, payload, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

/**
 * 8. FETCH USER DOCUMENTS
 * POST /user/document/all
 */
export const fetchUserDocuments = async (token) => {
  const response = await axios.post(
    `${BASE_URL}/user/document/all`,
    {
      page_number: 1,
      page_size: 50,
    },
    {
      headers: getAuthHeaders(token),
    }
  );
  return response.data;
};

/**
 * 8b. UPLOAD USER DOCUMENT
 * POST /all/user/document/add or /user/document/add
 */
export const uploadPartnerDocument = async (token, userId, documentName, file) => {
  const formData = new FormData();
  formData.append("name", documentName);
  formData.append("user_id", userId);
  formData.append("file", file);

  const response = await axios.post(`${BASE_URL}/all/user/document/add`, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
  });
  return response.data;
};

/**
 * Helper to fetch complete user profile by ID
 * GET /admin/user/id/:id
 */
export const fetchPartnerUserById = async (token, userId) => {
  const response = await axios.get(`${BASE_URL}/admin/user/id/${userId}`, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

/**
 * 9. COUPON VALIDATION / APPLY
 * POST /api/v1/coupons/validate or POST /api/v1/coupons/apply or GET /api/v1/coupons/code/:code
 */
export const validateCouponCode = async (token, couponCode, planId = null, amount = null) => {
  const code = String(couponCode).trim();
  if (!code) throw new Error("Coupon code is required");

  try {
    const response = await axios.post(
      `${BASE_URL}/api/v1/coupons/apply`,
      {
        coupon_code: code,
        code,
        subscription_plan_id: planId,
        plan_id: planId,
        amount,
      },
      { headers: getAuthHeaders(token) }
    );
    if (response.data?.statusType === "SUCCESS" || response.data?.status === "SUCCESS") {
      return response.data;
    }
  } catch (err) {
    console.warn("Error applying coupon via /apply, trying /code lookup:", err);
  }

  // Fallback: direct code lookup
  const res = await axios.get(`${BASE_URL}/api/v1/coupons/code/${encodeURIComponent(code)}`, {
    headers: getAuthHeaders(token),
  });
  return res.data;
};

/**
 * 10. CREATE RAZORPAY ORDER ID
 * POST /payments/getRazorPaymentOrderId
 */
export const createRazorpayPaymentOrder = async (token, userId, planId, couponCode = null) => {
  const payload = {
    user_id: String(userId),
    rating: 1,
    subscription_plan_id: planId,
    is_registration: true,
  };
  if (couponCode) {
    payload.coupon_code = couponCode;
  }

  const response = await axios.post(`${BASE_URL}/payments/getRazorPaymentOrderId`, payload, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};

/**
 * 11. VERIFY AND COMPLETE RAZORPAY PAYMENT
 * PUT /payments/saveAndVerifyPayment
 */
export const verifyAndSavePayment = async (token, verifyPayload) => {
  const response = await axios.put(`${BASE_URL}/payments/saveAndVerifyPayment`, verifyPayload, {
    headers: getAuthHeaders(token),
  });
  return response.data;
};
